import { useCallback } from 'react';

import { useManageQueryParameters } from './useManageQueryParameters';
import type { QueryParameterValue } from '../types/NavigateOptions';

type UseQueryParamReturn<QueryParameterValueType> = [
    QueryParameterValueType,
    (value?: QueryParameterValue) => void,
];

export function useQueryParam<QueryParameterName extends string>(
    name: QueryParameterName,
    parseArrays?: boolean,
): UseQueryParamReturn<string>;

export function useQueryParam<QueryParameterName extends string>(
    name: QueryParameterName,
    parseArrays: true,
): UseQueryParamReturn<string | string[]>;

/**
 * A hook for managing a single query parameter in the URL, with support for array values.
 *
 * @template QueryParameterName - The name of the query parameter (should extend string)
 * @param {QueryParameterName} name - The name of the query parameter to manage
 * @param {true} parseArrays - Must be true to enable array parsing
 * @returns {UseQueryParamReturn<string | string[]>} A tuple containing the parameter value (as string or string[]) and a setter function
 *
 * @example
 * ```typescript
 * // Usage with array parsing
 * const [tags, setTags] = useQueryParam('tags', true);
 * console.log(tags); // ['react', 'typescript'] (if URL is '?tags=react&tags=typescript')
 *
 * // Setting a single value
 * setTags('angular'); // Updates URL to '?tags=angular'
 * ```
 */
export function useQueryParam<QueryParameterName extends string>(
    name: QueryParameterName,
    parseArrays?: boolean,
): UseQueryParamReturn<string | string[]> {
    const { updateParam, searchParams } = useManageQueryParameters<QueryParameterName>(parseArrays);

    const setParam = useCallback(
        (value?: QueryParameterValue): void => {
            updateParam(name, value);
        },
        [updateParam, name],
    );

    return [searchParams[name], setParam];
}
